import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { postOrQueue } from "../lib/offlineQueue";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, LogOut as OutIcon, LogIn as InIcon, Camera } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");

export default function SecurityScan() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [branches, setBranches] = useState([]);
  const [acting, setActing] = useState("");
  useEffect(() => { if (isAdmin) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, [isAdmin]);
  const [token, setToken] = useState("");
  const [pass, setPass] = useState(null);
  const [odo, setOdo] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const lookup = async (t) => {
    try {
      const { data } = await api.get(`/gate/scan/${t}`);
      setPass(data); setOdo("");
      setActing(data.out_done ? data.in_branch_id : data.out_branch_id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          (decoded) => { stopScan(); setToken(decoded); lookup(decoded); }, () => {});
      } catch (e) {
        toast.error("Camera unavailable — enter the pass code manually");
        setScanning(false);
      }
    }, 100);
  };
  const stopScan = () => {
    if (scannerRef.current) { scannerRef.current.stop().catch(() => {}); scannerRef.current = null; }
    setScanning(false);
  };
  useEffect(() => () => stopScan(), []);

  const gateAction = async (dir) => {
    if (!odo) { toast.error("Enter the odometer reading you see on the car's dashboard"); return; }
    setBusy(true);
    try {
      const res = await postOrQueue(`/gate/scan/${token}/${dir}`, { odometer: parseInt(odo), ...(isAdmin && acting ? { acting_branch_id: acting } : {}) }, `Gate ${dir.toUpperCase()} · ${pass.reg_no}`);
      if (res.queued) {
        toast.info(`No network — gate ${dir.toUpperCase()} entry queued (odometer ${odo} km) and will sync automatically. Let the vehicle pass.`);
      } else {
        toast.success(`Gate ${dir.toUpperCase()} approved${res.data.mismatch ? " — odometer mismatch recorded & SM alerted" : ""}`);
      }
      setPass(null); setToken(""); setOdo("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-6 max-w-md mx-auto">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0E2240] text-white"><QrCode size={26} /></div>
        <h1 className="mt-3 font-heading text-2xl font-bold text-[#0E2240]">{isAdmin ? "Gate Scan — Admin" : "Security Gate"}</h1>
        <p className="text-xs text-slate-500">{isAdmin ? "Record a gate-out/in on behalf of any location. Every Admin scan is logged in Exceptions." : "Scan the gate pass QR, verify the vehicle, enter odometer"}</p>
      </div>

      {!pass && (
        <div className="mt-6 space-y-3">
          {scanning ? (
            <div>
              <div id="qr-reader" className="overflow-hidden rounded-2xl" />
              <button data-testid="stop-scan-btn" onClick={stopScan} className="mt-2 w-full rounded-xl border border-slate-300 py-3 text-sm font-bold text-slate-600">Stop camera</button>
            </div>
          ) : (
            <button data-testid="start-scan-btn" onClick={startScan}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#BB162B] py-5 text-base font-bold text-white active:scale-[0.98] transition-transform duration-150">
              <Camera size={20} /> Scan QR Gate Pass
            </button>
          )}
          <div className="flex gap-2">
            <input data-testid="manual-token-input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Or enter pass code manually"
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]" />
            <button data-testid="manual-lookup-btn" onClick={() => token && lookup(token)} className="rounded-xl bg-[#0E2240] px-5 text-sm font-bold text-white">Find</button>
          </div>
        </div>
      )}

      {pass && (
        <div className="mt-6 fade-up">
          <div data-testid="gate-pass-details" className="rounded-2xl bg-[#0E2240] p-5 text-white">
            <div className="text-[10px] uppercase tracking-widest text-[#BF9B30]">Verify against vehicle</div>
            <div className="mt-1 font-heading text-2xl font-bold">{pass.reg_no}</div>
            <div className="mt-2 space-y-1 text-sm text-slate-300">
              <div>{["transfer", "transfer_return"].includes(pass.td_type) ? "Movement" : (pass.kecs || []).length > 1 ? "Travelling KECs" : "KEC"}: <b className="text-white" data-testid="gate-pass-kecs">{(pass.kecs || [pass.kec_name]).join(" · ")}</b>{(pass.kecs || []).length > 1 && <span className="ml-1 inline-block whitespace-nowrap rounded-full bg-[#BF9B30] px-1.5 text-[9px] font-bold text-[#0E2240]">{pass.kecs.length} people — verify all</span>}</div>
              <div>{["transfer", "transfer_return"].includes(pass.td_type) ? "Driver" : `Customer${(pass.customers || []).length > 1 ? "s" : ""}`}: <b className="text-white">{(pass.customers || []).length ? pass.customers.join(", ") : (pass.customer_name || "").replace(/^Driver: /, "")}</b></div>
              <div>Type: <b className="text-white">{pass.td_type === "home" ? "Home TD" : pass.td_type === "transfer" ? "Inter-Branch Transfer" : pass.td_type === "transfer_return" ? "Loaned Car Returning Home" : pass.td_type === "connected" ? `Connected Drive — ${(pass.customers || []).length} customers` : pass.td_type === "utility" ? "Utility" : "Showroom TD"}</b></div>
              <div>Valid till: <b className={pass.expired ? "text-red-400" : "text-white"}>{dt(pass.validity)}{pass.expired && " (EXPIRED)"}</b></div>
              {pass.destination && <div>Destination: <b className="text-white">{pass.destination}</b></div>}
              {pass.out_branch_name !== pass.in_branch_name && <div>Gates: <b className="text-white">OUT {pass.out_branch_name} · IN {pass.in_branch_name}</b></div>}
            </div>
          </div>
          {pass.location_note && !isAdmin && (
            <div data-testid="gate-location-note" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-[#BB162B]">{pass.location_note} — this gate cannot record it.</div>
          )}
          {pass.movement_note && (
            <div data-testid="gate-movement-note" className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-800">{pass.movement_note}</div>
          )}
          {isAdmin && (
            <div data-testid="admin-acting-gate" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">Acting as gate</label>
              <select data-testid="admin-acting-gate-select" value={acting} onChange={(e) => setActing(e.target.value)} className="mt-1 w-full rounded-xl border border-amber-300 bg-white px-3 py-2.5 text-sm">
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.id === (pass.out_done ? pass.in_branch_id : pass.out_branch_id) ? " (expected gate)" : ""}</option>)}
              </select>
              <p className="mt-1 text-[10px] text-amber-700">Recorded as “by Admin on behalf of {branches.find((b) => b.id === acting)?.name || "…"}” and listed in Exceptions.</p>
            </div>
          )}
          <div className="mt-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Odometer reading on dashboard (km)</label>
            <input data-testid="gate-odometer-input" type="number" inputMode="numeric" value={odo} onChange={(e) => setOdo(e.target.value)} placeholder="Enter km shown on car"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-4 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#0E2240]" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="gate-out-btn" disabled={busy || pass.out_done || pass.can_out === false} onClick={() => gateAction("out")}
              className="flex items-center justify-center gap-2 rounded-xl bg-[#BB162B] py-4 text-sm font-bold text-white disabled:opacity-40 active:scale-[0.98] transition-transform duration-150">
              <OutIcon size={17} /> Scan OUT
            </button>
            <button data-testid="gate-in-btn" disabled={busy || !pass.out_done || pass.in_done || pass.can_in === false} onClick={() => gateAction("in")}
              className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-4 text-sm font-bold text-white disabled:opacity-40 active:scale-[0.98] transition-transform duration-150">
              <InIcon size={17} /> Scan IN
            </button>
          </div>
          <button data-testid="cancel-pass-btn" onClick={() => { setPass(null); setToken(""); }} className="mt-3 w-full py-2 text-sm text-slate-500">Cancel</button>
        </div>
      )}
    </div>
  );
}
